import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { DateRange } from 'react-date-range'
import { useMediaQuery } from 'react-responsive'
import PropTypes from 'prop-types'
import classnames from 'classnames'

import Select from '../../../../components/Select'
import Button from '../../../../components/Button'
import SelectDropdown from '../../../../components/SelectDropdown'
import Icon from '../../../../components/Icon'

import { ReactComponent as CalendarIcon } from '../../../../assets/icons/calendar-icon2.svg'
import useDateRange from '../hooks'

import { getStaticRangeLabel } from '../helpers'
import { getInitialDatePeriod } from '../../../../helpers/date'
import { initialDatePeriod } from '../../../../constants/dates'
import {
  CAMPAIGN_CUSTOM_RANGE_VALUE,
  DATE_STATIC_PRESET,
  LIFETIME_DATE_PRESET,
  staticPeriodsList,
  staticPeriodsListWithCustom
} from '../../../../constants/selectLists/listItemFiltersList'

import useDateRangeStyles from '../styles'
import useStyles from './styles'
import { phonesDownSize } from '../../../../styles/const/breakpoints'

const initialStaticRange = null
const inputRanges = []

function DateRangeWithStaticList({ setDateRangeAction, dateRangeSelector }) {
  // component select either DateRange from calendar either staticRange from staticPeriodsList
  // on staticRange selection the component closes
  // staticPeriodsList on Mobile shows as a regular select, but it have additional value - custom
  // when custom value selected the DatePicker will appear
  const dateRangeClasses = useDateRangeStyles()
  const classes = useStyles()
  const dispatch = useDispatch()

  const savedDateRange = useSelector(dateRangeSelector)
  const initStaticRange = savedDateRange[DATE_STATIC_PRESET]

  const [datePeriod, setDatePeriod] = useState(getInitialDatePeriod(savedDateRange))
  const [staticRange, setStaticRange] = useState(initStaticRange)

  const isMobile = useMediaQuery({ maxWidth: phonesDownSize })

  const { isOpen, setIsOpen, rangeColors, dateRangeLabel } = useDateRange(datePeriod, isMobile)

  const handleDropdownVisibility = useCallback(
    show => {
      setIsOpen(show)
      if (isOpen && !show) {
        // return to default on close without update
        setDatePeriod(getInitialDatePeriod(savedDateRange))
        setStaticRange(initStaticRange)
      }
    },
    [isOpen, setIsOpen, savedDateRange, initStaticRange]
  )

  const handleMobileDateChange = useCallback(
    ({ value: newStaticRange }) => {
      setStaticRange(newStaticRange)
      if (newStaticRange === CAMPAIGN_CUSTOM_RANGE_VALUE) {
        setIsOpen(true)
      } else {
        dispatch(setDateRangeAction({ [DATE_STATIC_PRESET]: newStaticRange }))
      }
    },
    [dispatch, setIsOpen, setDateRangeAction]
  )

  const handleDateRangeChange = useCallback(item => {
    setDatePeriod(item.selection)
    setStaticRange(initialStaticRange)
  }, [])

  const handleSave = useCallback(
    range => {
      setIsOpen(false)
      dispatch(setDateRangeAction(range))
      if (range[DATE_STATIC_PRESET] === CAMPAIGN_CUSTOM_RANGE_VALUE) {
        // set to default when custom was selected but closed without selection range)
        setStaticRange(LIFETIME_DATE_PRESET)
      }
    },
    [dispatch, setDateRangeAction, setIsOpen]
  )

  const handleStaticRangeSelect = useCallback(
    range => {
      setStaticRange(range)
      handleSave({ [DATE_STATIC_PRESET]: range })
    },
    [handleSave]
  )

  const handleUpdateSelect = useCallback(() => {
    const { startDate, endDate } = datePeriod
    const range = staticRange ? { [DATE_STATIC_PRESET]: staticRange } : { startDate, endDate }
    handleSave(range)
  }, [datePeriod, staticRange, handleSave])

  const ranges = useMemo(() => [staticRange ? initialDatePeriod : datePeriod], [staticRange, datePeriod])

  return (
    <div>
      {isMobile && (
        <Select
          isSearchable={false}
          className={dateRangeClasses.dateRangeMobile}
          placeholder="Date range"
          formatValue={false}
          // represent full dateRange for Custom staticRange
          value={
            staticRange
              ? { label: getStaticRangeLabel(staticRange), value: staticRange }
              : { label: dateRangeLabel, value: CAMPAIGN_CUSTOM_RANGE_VALUE }
          }
          // close DateRange on Select opening
          onMenuOpen={() => handleDropdownVisibility(false)}
          options={staticPeriodsListWithCustom}
          onChange={handleMobileDateChange}
          Icon={
            <Icon className={classes.calendarIcon}>
              <CalendarIcon />
            </Icon>
          }
          showIndicator={false}
          isThickBorder
        />
      )}
      <SelectDropdown
        className={classnames(classes.pageDateRange, dateRangeClasses.dateRange)}
        value={staticRange ? getStaticRangeLabel(staticRange) : dateRangeLabel}
        placeholder="Date range"
        isManuallyOpened={isOpen}
        onVisibilityChange={handleDropdownVisibility}
        Icon={
          <Icon className={classes.calendarIcon}>
            <CalendarIcon />
          </Icon>
        }
        showIndicator={false}
        isThickBorder
      >
        <div className={dateRangeClasses.dateRangeDropdown}>
          <div className={dateRangeClasses.dateRanges}>
            {!isMobile && (
              <ul className={dateRangeClasses.staticRangeList}>
                {staticPeriodsList.map(({ value, label }) => {
                  return (
                    <li
                      key={value}
                      className={classnames(dateRangeClasses.staticRangeItem, {
                        [dateRangeClasses.rangeItemActive]: value === staticRange
                      })}
                      onClick={() => handleStaticRangeSelect(value)}
                    >
                      {label}
                    </li>
                  )
                })}
              </ul>
            )}
            <DateRange
              onChange={handleDateRangeChange}
              className={dateRangeClasses.range}
              showDateDisplay={false}
              months={isMobile ? 1 : 2}
              ranges={ranges}
              inputRanges={inputRanges}
              rangeColors={rangeColors}
              direction="horizontal"
            />
          </div>
          <Button onClick={handleUpdateSelect} className={dateRangeClasses.saveBtn}>
            Update
          </Button>
        </div>
      </SelectDropdown>
    </div>
  )
}

DateRangeWithStaticList.propTypes = {
  setDateRangeAction: PropTypes.func,
  dateRangeSelector: PropTypes.func
}

export default DateRangeWithStaticList
